import { useState, useEffect, useCallback } from "react";
import { debounce } from "lodash";

const useCart = () => {
  const [selectedItems, setSelectedItems] = useState({});
  const [cartItems, setCartItems] = useState([]);
  const [filteredCart, setFilteredCart] = useState([]);
  const [popupMessage, setPopupMessage] = useState(null);
  const [showPopup, setShowPopup] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userId, setUserId] = useState(null);
  const [isRemoving, setIsRemoving] = useState(false);

  const checkLoginStatus = () => {
    const token = localStorage.getItem("userToken");
    const uid = localStorage.getItem("userId");
    const loggedIn = !!token && uid !== null && !isNaN(Number(uid));
    setIsLoggedIn(loggedIn);
    setUserId(loggedIn ? Number(uid) : null);
  };

  useEffect(() => {
    checkLoginStatus();
    window.addEventListener("storage", checkLoginStatus);
    window.addEventListener("userLoggedIn", checkLoginStatus);
    return () => {
      window.removeEventListener("storage", checkLoginStatus);
      window.removeEventListener("userLoggedIn", checkLoginStatus);
    };
  }, []);

  const loadCart = useCallback(async () => {
    if (!userId) return;
    try {
      const response = await fetch(
        `https://inaaniserver-50c4305cc7f7.herokuapp.com/order/by_status?user_id=${userId}&status=add to cart`
      );
      if (!response.ok) {
        throw new Error(`Failed to fetch cart: ${response.status}`);
      }
      const data = await response.json();
      console.log("loadCart API response:", data);
      let rawCartItems = [];
      if (Array.isArray(data.data)) {
        data.data.forEach((order) => {
          Object.values(order).forEach((product) => {
            if (product && typeof product === "object" && "product_id" in product) {
              console.log("Product data:", product);
              rawCartItems.push({
                ...product,
                id: product.product_id ?? `temp-${Math.random()}`,
                order_id: product.order_id || product.id,
                product_name: product.product_name || product.name || "No Name",
                quantity: product.quantity?.amount || product.quantity || 1,
                price: parseFloat(product.price) || 0.0,
                storeName: product.store_name || "Ina-Ani",
              });
            }
          });
        });
      }
      const mergedCart = [];
      const productMap = {};
      rawCartItems.forEach((item) => {
        if (productMap[item.id]) {
          productMap[item.id].quantity += item.quantity;
          productMap[item.id].order_ids = [
            ...(productMap[item.id].order_ids || []),
            item.order_id,
          ].filter((id) => id !== undefined);
        } else {
          productMap[item.id] = {
            ...item,
            order_ids: item.order_id ? [item.order_id] : [],
          };
          mergedCart.push(productMap[item.id]);
        }
      });
      console.log("Merged cart:", mergedCart);
      setCartItems(mergedCart);
    } catch (error) {
      console.error("Failed to fetch cart:", error.message || error);
      setPopupMessage("Failed to load cart. Please try again.");
    }
  }, [userId]);

  useEffect(() => {
    setFilteredCart(cartItems);
  }, [cartItems]);

  useEffect(() => {
    if (userId) {
      loadCart();
    } else {
      setCartItems([]);
    }
  }, [userId, loadCart]);

  const handleUpdateCartQuantity = (productId, newQuantity) => {
    // Update quantity locally without making an API call
    setCartItems((prevCart) =>
      prevCart.map((item) =>
        item.id === productId ? { ...item, quantity: newQuantity } : item
      )
    );
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedHandleQuantityChange = useCallback(
    debounce((productId, action, event) => {
      const currentItem = cartItems.find((item) => item.id === productId);
      if (!currentItem) return;

      let newQuantity = currentItem.quantity;
      if (action === "increment") {
        newQuantity += 1;
      } else if (action === "decrement") {
        newQuantity = Math.max(1, currentItem.quantity - 1);
      } else if (event) {
        newQuantity = Math.max(1, parseInt(event.target.value, 10) || 1);
      }

      handleUpdateCartQuantity(productId, newQuantity);
    }, 300),
    []
  );

  const handleQuantityChange = (productId, action, event = null) => {
    debouncedHandleQuantityChange(productId, action, event);
  };

  const handleAddToCart = async (product) => {
    const token = localStorage.getItem("userToken");
    if (!token || !userId) {
      setShowPopup(true);
      return;
    }

    const quantity = selectedItems[product.id]?.quantity || 1;
    const price = product.price;
    const total_amount = price * quantity;

    const previousItems = [...cartItems];
    setCartItems((prevItems) => {
      const existingItem = prevItems.find((item) => item.id === product.id);
      if (existingItem) {
        return prevItems.map((item) =>
          item.id === product.id
            ? { ...item, quantity: item.quantity + quantity }
            : item
        );
      }
      return [
        ...prevItems,
        {
          id: product.id,
          product_name: product.product_name,
          price: product.price,
          quantity: quantity,
          image: product.image,
          storeName: "Ina-Ani",
        },
      ];
    });

    const orderData = {
      product_id: product.id,
      user_id: userId,
      quantity,
      price,
      total_amount,
      address: null,
      status: "add to cart",
    };

    try {
      const response = await fetch(
        "https://inaaniserver-50c4305cc7f7.herokuapp.com/order/add",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(orderData),
        }
      );

      const data = await response.json();
      if (!response.ok) {
        setPopupMessage(data.error || "Failed to add to cart.");
        setCartItems(previousItems);
      } else {
        setPopupMessage("Item added to cart successfully!");
        await loadCart();
      }
    } catch (error) {
      console.error("Fetch error:", error);
      setPopupMessage("Network error. Please try again.");
      setCartItems(previousItems);
    }
  };

  const handleAddToCartForBuyNow = async (product) => {
    const token = localStorage.getItem("userToken");
    if (!token || !userId) {
      setShowPopup(true);
      return false;
    }

    const quantity = selectedItems[product.id]?.quantity || 1;
    const price = product.price;
    const total_amount = price * quantity;

    const previousItems = [...cartItems];
    setCartItems((prevItems) => {
      const existingItem = prevItems.find((item) => item.id === product.id);
      if (existingItem) {
        return prevItems.map((item) =>
          item.id === product.id
            ? { ...item, quantity: item.quantity + quantity }
            : item
        );
      }
      return [
        ...prevItems,
        {
          id: product.id,
          product_name: product.product_name,
          price: product.price,
          quantity: quantity,
          image: product.image,
          storeName: "Ina-Ani",
        },
      ];
    });

    const orderData = {
      product_id: product.id,
      user_id: userId,
      quantity,
      price,
      total_amount,
      address: null,
      status: "add to cart",
    };

    try {
      const response = await fetch(
        "https://inaaniserver-50c4305cc7f7.herokuapp.com/order/add",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(orderData),
        }
      );

      if (!response.ok) {
        setCartItems(previousItems);
        return false;
      }
      await loadCart();
      return true;
    } catch (error) {
      console.error("Fetch error:", error);
      setCartItems(previousItems);
      return false;
    }
  };

  const removeItems = async (productIds) => {
    if (isRemoving) return;
    setIsRemoving(true);

    const token = localStorage.getItem("userToken");
    if (!token || !userId) {
      setShowPopup(true);
      setPopupMessage("Please log in to remove items.");
      setIsRemoving(false);
      return;
    }

    const orderIds = cartItems
      .filter((item) => productIds.includes(item.id))
      .flatMap((item) => item.order_ids || []);

    console.log("Order IDs to delete:", orderIds);

    if (orderIds.length === 0) {
      setPopupMessage("No valid order IDs found for selected items.");
      setIsRemoving(false);
      return;
    }

    setCartItems((prevItems) => prevItems.filter((item) => !productIds.includes(item.id)));

    try {
      await Promise.all(
        orderIds.map(async (orderId) => {
          const response = await fetch(
            `https://inaaniserver-50c4305cc7f7.herokuapp.com/order/delete?id=${orderId}`,
            {
              method: "POST",
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );


          if (!response.ok) {
            const data = await response.json();
            console.log(`Delete response for order ${orderId}:`, {
              status: response.status,
              data,
            });
            throw new Error(data.message || `Failed to remove order ${orderId}`);
          }
        })
      );
      setPopupMessage("Items removed successfully!");
      await loadCart();
    } catch (error) {
      console.error("Failed to remove items:", error);
      setPopupMessage("Failed to remove items: " + error.message);
    } finally {
      setIsRemoving(false);
    }
  };

  const syncCartWithServer = async () => {
    const token = localStorage.getItem("userToken");
    if (!token || !userId) {
      setShowPopup(true);
      setPopupMessage("Please log in to sync cart.");
      return false;
    }

    try {
      await Promise.all(
        cartItems.map(async (item) => {
          const orderData = {
            product_id: item.id,
            user_id: userId,
            quantity: item.quantity,
            price: item.price,
            total_amount: item.price * item.quantity,
            address: null,
            status: "add to cart",
          };

          const response = await fetch(
            "https://inaaniserver-50c4305cc7f7.herokuapp.com/order/add",
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
              },
              body: JSON.stringify(orderData),
            }
          );

          const data = await response.json();
          if (!response.ok) {
            throw new Error(data.error || "Failed to sync item to cart.");
          }
        })
      );
      await loadCart();
      return true;
    } catch (error) {
      console.error("Sync error:", error);
      setPopupMessage("Failed to sync cart: " + error.message);
      return false;
    }
  };

  const closePopup = () => setPopupMessage(null);

  return {
    selectedItems,
    setSelectedItems,
    handleQuantityChange,
    handleAddToCart,
    handleAddToCartForBuyNow,
    cartItems,
    filteredCart,
    loadCart,
    popupMessage,
    closePopup,
    showPopup,
    setShowPopup,
    isLoggedIn,
    removeItems,
    isRemoving,
    syncCartWithServer,
  };
};

export default useCart;